import { createHash } from "node:crypto";
import {
  prisma,
  Provider,
  CommunicationLogStatus,
  TemplateV2For,
  TemplateV2Level,
  TemplateV2Type,
  TriggerV2Type,
  type Prisma,
} from "client";
import {
  aggregateCommunicationLogs,
  type LeanCommunicationLog,
} from "../communicationSettings/stats.pure";

type CommunicationLogDatabaseClient = typeof prisma;

const COMMUNICATION_V2_TRIGGERS = [
  TriggerV2Type.ON_ANNOUNCEMENT_SENT,
  TriggerV2Type.ON_NEWSLETTER_SENT,
] as const;

const COMMUNICATION_V2_CHANNEL_TYPES = [
  TemplateV2Type.EMAIL,
  TemplateV2Type.SMS,
  TemplateV2Type.WHATSAPP,
  TemplateV2Type.NOTIFICATION,
] as const;

const mapTemplateV2TypeToDisplayType = (type: TemplateV2Type): string => {
  if (type === TemplateV2Type.EMAIL) return "EMAIL_DESIGN";
  return type;
};

type TemplateLog = LeanCommunicationLog & {
  id: string;
  provider: Provider;
  status: CommunicationLogStatus;
};

type TemplateStats = ReturnType<typeof aggregateCommunicationLogs> & {
  audience: number;
};

const aggregateLogStats = (
  logs: TemplateLog[],
  isPush: boolean,
  audience = 0
): TemplateStats => ({
  ...aggregateCommunicationLogs(logs, isPush),
  audience,
});

// GET event communication templates (TemplateV2) with aggregated stats by eventId
export const getTemplatesWithStatsByEventId = async (
  eventId: string
): Promise<{
  templates?: Array<{
    id: string;
    name: string;
    type: string;
    trigger: TriggerV2Type | null;
    stats: TemplateStats;
  }>;
  error?: any;
}> => {
  try {
    const templates = await prisma.templateV2.findMany({
      where: {
        level: TemplateV2Level.EVENT,
        scopeId: eventId,
        templateFor: TemplateV2For.COMMUNICATION_COMMUNICATION,
        type: { in: [...COMMUNICATION_V2_CHANNEL_TYPES] },
        AND: [
          {
            OR: [{ deletedAt: null }, { deletedAt: { isSet: false } }],
          },
          {
            OR: [
              { type: TemplateV2Type.NOTIFICATION },
              { trigger: { in: [...COMMUNICATION_V2_TRIGGERS] } },
            ],
          },
        ],
      },
      select: {
        id: true,
        name: true,
        type: true,
        trigger: true,
        config: true,
      },
      orderBy: [{ updatedAt: "desc" }, { createdAt: "desc" }],
    });

    const templateIds = templates.map((t) => t.id);
    const logsByTemplateId = new Map<string, TemplateLog[]>();
    const audienceProfileIdsByTemplateId = new Map<string, Set<string>>();
    if (templateIds.length) {
      const notificationTemplateIds = templates
        .filter((template) => template.type === TemplateV2Type.NOTIFICATION)
        .map((template) => template.id);
      const [logs, notificationCampaigns] = await Promise.all([
        prisma.communicationLog.findMany({
          where: {
            templateV2Id: { in: templateIds },
          },
          select: {
            id: true,
            templateV2Id: true,
            provider: true,
            status: true,
            recipientProfileId: true,
            email: true,
            stats: true,
          },
        }),
        notificationTemplateIds.length
          ? prisma.communicationSettings.findMany({
              where: { templateV2Id: { in: notificationTemplateIds } },
              select: { templateV2Id: true, audienceProfileIds: true },
            })
          : [],
      ]);

      for (const log of logs) {
        const key = log.templateV2Id ?? "";
        if (!key) continue;
        const list = logsByTemplateId.get(key);
        if (list) list.push(log);
        else logsByTemplateId.set(key, [log]);
      }
      for (const campaign of notificationCampaigns) {
        const key = campaign.templateV2Id ?? "";
        if (!key) continue;
        // ponytail: the indexed templateV2Id query lets Overview de-dupe resends
        // in memory; use a Mongo aggregation if a template is reused at high volume.
        const profileIds =
          audienceProfileIdsByTemplateId.get(key) ?? new Set<string>();
        for (const profileId of campaign.audienceProfileIds) {
          profileIds.add(profileId);
        }
        audienceProfileIdsByTemplateId.set(key, profileIds);
      }
    }

    const templatesWithStats = templates.map((template) => ({
      id: template.id,
      name: template.name,
      type: mapTemplateV2TypeToDisplayType(template.type),
      trigger: template.trigger,
      config: template.config,
      stats: aggregateLogStats(
        logsByTemplateId.get(template.id) ?? [],
        template.type === TemplateV2Type.NOTIFICATION,
        audienceProfileIdsByTemplateId.get(template.id)?.size ?? 0
      ),
    }));

    return { templates: templatesWithStats };
  } catch (error) {
    console.error("Error fetching templates with stats:", error);
    return { error };
  }
};

// GET CommunicationLogs by templateId with pagination
// CREATE a new communication log entry
/** @deprecated Prefer createCommunicationLogV2 for TemplateV2 sends. */
export const createCommunicationLog = async (params: {
  email: string;
  phoneNumber?: string | null;
  templateId?: string;
  templateV2Id?: string;
  communicationSettingsId?: string | null;
  provider: Provider;
  providerMessageId: string;
  status: CommunicationLogStatus;
  failedReason?: string | null;
  emailPayload?: {
    subject?: string;
    to: string;
    attachments?: number;
  };
  organizationId?: string | null;
}): Promise<{
  log?: any;
  error?: any;
}> => {
  try {
    const communicationLog = await prisma.communicationLog.create({
      data: {
        email: params.email,
        phoneNumber: params.phoneNumber || null,
        templateV2Id: params.templateV2Id ?? undefined,
        communicationSettingsId: params.communicationSettingsId || null,
        provider: params.provider,
        providerMessageId: params.providerMessageId,
        status: params.status,
        organizationId: params.organizationId || null,
        failedReason: params.failedReason || null,
        stats: {
          opened: 0,
          clicked: 0,
          bounced: 0,
          delivered: 0,
          spam: 0,
        },
        emailPayload: params.emailPayload
          ? JSON.stringify(params.emailPayload)
          : "",
      },
    });
    console.log(
      `✅ Created communication log: ${communicationLog.id} with providerMessageId: "${communicationLog.providerMessageId}" status: ${communicationLog.status}`
    );
    return { log: communicationLog };
  } catch (error) {
    console.error("Error creating communication log:", error);
    return { error };
  }
};

// CREATE a communication log entry for a v2 template
export const createCommunicationLogV2 = async (params: {
  email: string;
  phoneNumber?: string | null;
  templateV2Id: string;
  communicationSettingsId?: string | null;
  provider: Provider;
  providerMessageId: string;
  status: CommunicationLogStatus;
  failedReason?: string | null;
  emailPayload?: {
    subject?: string;
    to: string;
    attachments?: number;
  };
  organizationId?: string | null;
}): Promise<{
  log?: any;
  error?: any;
}> => {
  try {
    const communicationLog = await prisma.communicationLog.create({
      data: {
        email: params.email,
        phoneNumber: params.phoneNumber || null,
        templateV2Id: params.templateV2Id,
        communicationSettingsId: params.communicationSettingsId ?? null,
        provider: params.provider,
        providerMessageId: params.providerMessageId,
        status: params.status,
        organizationId: params.organizationId || null,
        failedReason: params.failedReason || null,
        stats: {
          opened: 0,
          clicked: 0,
          bounced: 0,
          delivered: 0,
          spam: 0,
        },
        emailPayload: params.emailPayload
          ? JSON.stringify(params.emailPayload)
          : "",
      },
    });
    console.log(
      `✅ Created v2 communication log: ${communicationLog.id} | providerMessageId: "${communicationLog.providerMessageId}" | status: ${communicationLog.status}`
    );
    return { log: communicationLog };
  } catch (error) {
    console.error("Error creating v2 communication log:", error);
    return { error };
  }
};

export async function createIdempotentCommunicationLog(
  params: {
    email?: string | null;
    phoneNumber?: string | null;
    templateV2Id: string;
    communicationSettingsId: string;
    provider: Provider;
    providerMessageId: string;
    status: CommunicationLogStatus;
    failedReason?: string | null;
    recipientProfileId?: string | null;
    occurrenceKey?: string | null;
    idempotencyKey: string;
    payload?: Prisma.InputJsonValue;
    /** V2 ledgers use the deterministic document id and need no legacy scan. */
    skipLegacyLookup?: boolean;
  },
  db: CommunicationLogDatabaseClient = prisma
): Promise<{ created: boolean; log?: { id: string } }> {
  const deterministicId = deterministicCommunicationLogId(
    params.idempotencyKey
  );
  const deterministicExisting = await db.communicationLog.findUnique({
    where: { id: deterministicId },
    select: { id: true },
  });
  if (deterministicExisting) {
    return { created: false, log: deterministicExisting };
  }
  if (!params.skipLegacyLookup) {
    const existing = await findCommunicationLogByIdempotencyKey(
      params.idempotencyKey,
      db
    );
    if (existing) return { created: false, log: existing };
  }

  try {
    const log = await db.communicationLog.create({
      data: {
        id: deterministicId,
        email: params.email ?? null,
        phoneNumber: params.phoneNumber ?? null,
        templateV2Id: params.templateV2Id,
        communicationSettingsId: params.communicationSettingsId,
        provider: params.provider,
        providerMessageId: params.providerMessageId,
        status: params.status,
        failedReason: params.failedReason ?? null,
        recipientProfileId: params.recipientProfileId ?? null,
        occurrenceKey: params.occurrenceKey ?? null,
        idempotencyKey: params.idempotencyKey,
        payload: params.payload,
        emailPayload: "",
        stats: {
          opened: 0,
          clicked: 0,
          bounced: 0,
          delivered: params.status === CommunicationLogStatus.DELIVERED ? 1 : 0,
          spam: 0,
        },
      },
      select: { id: true },
    });
    return { created: true, log };
  } catch (error) {
    const deterministicDuplicate = await db.communicationLog.findUnique({
      where: { id: deterministicId },
      select: { id: true },
    });
    if (deterministicDuplicate) {
      return { created: false, log: deterministicDuplicate };
    }
    const duplicate = await findCommunicationLogByIdempotencyKey(
      params.idempotencyKey,
      db
    );
    if (duplicate) return { created: false, log: duplicate };
    throw error;
  }
}

export function deterministicCommunicationLogId(idempotencyKey: string) {
  return createHash("sha256").update(idempotencyKey).digest("hex").slice(0, 24);
}

async function findCommunicationLogByIdempotencyKey(
  idempotencyKey: string,
  db: CommunicationLogDatabaseClient = prisma
): Promise<{ id: string } | undefined> {
  // Keep the partial-index predicate explicit; Prisma findFirst emits $expr
  // and MongoDB otherwise falls back to a collection scan.
  const rows = (await db.communicationLog.aggregateRaw({
    pipeline: [
      {
        $match: {
          $and: [{ idempotencyKey }, { idempotencyKey: { $type: "string" } }],
        },
      },
      { $limit: 1 },
      { $project: { _id: 1 } },
    ],
  })) as unknown as Array<{ _id?: string | { $oid?: string } }>;
  const rawId = rows[0]?._id;
  const id = typeof rawId === "string" ? rawId : rawId?.$oid;
  return id ? { id } : undefined;
}

// FIND a communication log by provider message ID
export const findCommunicationLogByMessageId = async (
  providerMessageId: string
): Promise<{
  log?: any;
  error?: any;
}> => {
  try {
    const log = await prisma.communicationLog.findFirst({
      where: {
        providerMessageId,
      },
    });
    return { log };
  } catch (error) {
    console.error("Error finding communication log by messageId:", error);
    return { error };
  }
};

// UPDATE a communication log
export const updateCommunicationLog = async (
  id: string,
  data: {
    status?: CommunicationLogStatus;
    failedReason?: string;
    providerMessageId?: string;
    stats?: {
      opened?: number;
      clicked?: number;
      bounced?: number;
      delivered?: number;
      spam?: number;
    };
  }
): Promise<{
  log?: any;
  error?: any;
}> => {
  try {
    const updatedLog = await prisma.communicationLog.update({
      where: { id },
      data,
    });
    console.log(
      `✅ Updated log ${id}: status=${updatedLog.status}, stats=`,
      updatedLog.stats
    );
    return { log: updatedLog };
  } catch (error) {
    console.error("Error updating communication log:", error);
    return { error };
  }
};

// GET CommunicationLogs by templateId with pagination
export const getCommunicationLogsByTemplateId = async (
  templateId: string,
  page: number = 1,
  limit: number = 8
): Promise<{
  logs?: Array<{
    id: string;
    email: string | null;
    phoneNumber: string | null;
    status: string;
    provider: string;
    stats: any;
    failedReason: string | null;
    createdAt: Date;
    updatedAt: Date;
  }>;
  pagination?: {
    total: number;
    page: number;
    limit: number;
    totalPages: number;
    hasNextPage: boolean;
    hasPreviousPage: boolean;
  };
  error?: any;
}> => {
  try {
    const skip = (page - 1) * limit;

    // Get total count
    const total = await prisma.communicationLog.count({
      where: {
        templateV2Id: templateId,
      },
    });

    // Get paginated logs - only select fields used by frontend
    const logs = await prisma.communicationLog.findMany({
      where: {
        templateV2Id: templateId,
      },
      select: {
        id: true,
        email: true,
        phoneNumber: true,
        status: true,
        provider: true,
        stats: true,
        failedReason: true,
        createdAt: true,
        updatedAt: true,
      },
      orderBy: {
        createdAt: "desc",
      },
      skip,
      take: limit,
    });

    const totalPages = Math.ceil(total / limit);

    return {
      logs,
      pagination: {
        total,
        page,
        limit,
        totalPages,
        hasNextPage: page < totalPages,
        hasPreviousPage: page > 1,
      },
    };
  } catch (error) {
    console.error("Error fetching communication logs by template:", error);
    return { error };
  }
};
